/**
 * Zakończenie zatrudnienia (Etap 15) — jeden fakt, z którego wynika wszystko.
 *
 * Tryb rozwiązania i strona inicjująca nie są ozdobą: wchodzą do bloku V
 * formularza ZUS ZWUA i muszą zgadzać się ze świadectwem pracy. Dlatego pyta
 * o nie formularz, a nie domyśla się ich dokument.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Input, Modal, Select, useToast } from '@k1p4/ui'
import { TERMINATION_MODES, needsInitiator, type TerminationMode } from '@k1p4/shared'
import { ApiError, api } from '../../api/client'
import styles from './EmployeeProfile.module.css'

export interface OffboardingModalProps {
  employeeId: string
  onClose: () => void
}

const MODES = Object.keys(TERMINATION_MODES) as TerminationMode[]

export function OffboardingModal({ employeeId, onClose }: OffboardingModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = { endDate: useId(), mode: useId(), initiator: useId(), reason: useId() }

  const [form, setForm] = useState({
    endDate: '',
    mode: 'porozumienie' as TerminationMode,
    initiator: 'pracownik' as 'pracownik' | 'pracodawca',
    reason: '',
  })
  const [error, setError] = useState<string | null>(null)

  const record = useMutation({
    mutationFn: () =>
      api.recordOffboarding(employeeId, {
        endDate: form.endDate,
        mode: form.mode,
        ...(needsInitiator(form.mode) ? { initiator: form.initiator } : {}),
        ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      }),
    onSuccess: (result) => {
      toast.show(t('profile.offboarding.recorded', { date: result.deregisterOn }), {
        tone: 'info',
      })
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] })
      onClose()
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiError && cause.code === 'NOT_EMPLOYED') {
        setError('profile.offboarding.errorNotEmployed')
      } else if (cause instanceof ApiError && cause.code === 'VALIDATION_ERROR') {
        setError('profile.offboarding.errorDate')
      } else {
        setError('profile.offboarding.errorGeneric')
      }
    },
  })

  return (
    <Modal
      open
      title={t('profile.offboarding.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={record.isPending || !/^\d{4}-\d{2}-\d{2}$/.test(form.endDate)}
            onClick={() => record.mutate()}
          >
            {t('profile.offboarding.submit')}
          </Button>
        </>
      }
    >
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.annexBanner}>
          {t(error)}
        </InfoBanner>
      ) : null}
      <p className={styles.annexTitle}>{t('profile.offboarding.lead')}</p>
      <FormField
        label={t('profile.offboarding.endDate')}
        htmlFor={ids.endDate}
        hint={t('profile.offboarding.endDateHint')}
      >
        <Input
          id={ids.endDate}
          type="date"
          value={form.endDate}
          onChange={(event) => setForm({ ...form, endDate: event.target.value })}
        />
      </FormField>
      <FormField label={t('profile.offboarding.mode')} htmlFor={ids.mode}>
        <Select
          id={ids.mode}
          value={form.mode}
          onChange={(event) =>
            setForm({ ...form, mode: event.target.value as TerminationMode })
          }
        >
          {MODES.map((mode) => (
            <option key={mode} value={mode}>
              {t(`profile.offboarding.modes.${mode}`)}
            </option>
          ))}
        </Select>
      </FormField>
      {needsInitiator(form.mode) ? (
        <FormField
          label={t('profile.offboarding.initiator')}
          htmlFor={ids.initiator}
          hint={t('profile.offboarding.initiatorHint')}
        >
          <Select
            id={ids.initiator}
            value={form.initiator}
            onChange={(event) =>
              setForm({ ...form, initiator: event.target.value as typeof form.initiator })
            }
          >
            <option value="pracownik">{t('profile.offboarding.initiators.pracownik')}</option>
            <option value="pracodawca">{t('profile.offboarding.initiators.pracodawca')}</option>
          </Select>
        </FormField>
      ) : null}
      <FormField label={t('profile.offboarding.reason')} htmlFor={ids.reason}>
        <Input
          id={ids.reason}
          value={form.reason}
          onChange={(event) => setForm({ ...form, reason: event.target.value })}
        />
      </FormField>
    </Modal>
  )
}
